import React, { useCallback, useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Sparkles, Plus, Edit2, Trash2, Power, RefreshCw, Search, Loader2,
  Tag, Lock,
} from 'lucide-react';

/**
 * Expense Categories — admin workspace.
 *
 * Lets an admin curate the per-tenant Smart-Mapping registry:
 *   • Seed the KSA defaults pack once.
 *   • Add custom categories (e.g. "Saudi Aramco Fuel Card").
 *   • Link each to a default GL account, VAT rate, cost-centre,
 *     payment method, and default bill kind.
 *   • Deactivate (never hard-delete) — historical bills keep their
 *     denormalised ``expense_category_name`` snapshot safely.
 */
const TONE_DOT = {
  amber: 'bg-amber-500', red: 'bg-red-500', blue: 'bg-blue-500',
  indigo: 'bg-indigo-500', emerald: 'bg-emerald-500', purple: 'bg-purple-500',
  teal: 'bg-teal-500', slate: 'bg-slate-500',
};
const KIND_LABEL = {
  supplier: 'مَوَرِّد', utility: 'مَرافِق', telecom: 'اتِّصالات',
  government: 'حُكومي', municipality: 'بَلَدِيَّة', customs: 'جَمارِك',
  insurance: 'تَأمين', subscription: 'اشتِراك', rent: 'إيجار',
  traffic_fine: 'مُرور', professional: 'مِهَني', other: 'أُخرى',
};

export default function ExpenseCategories({ user }) {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'pilot_admin';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cats, accs, ccs] = await Promise.all([
        axiosInstance.get('/expense-categories', { params: { include_inactive: true } }),
        axiosInstance.get('/accounts').catch(() => ({ data: [] })),
        axiosInstance.get('/cost-centers').catch(() => ({ data: [] })),
      ]);
      setItems(cats.data.items || []);
      setAccounts(Array.isArray(accs.data) ? accs.data : (accs.data?.items || []));
      setCostCenters(Array.isArray(ccs.data) ? ccs.data : (ccs.data?.items || []));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'تَعَذَّر التَّحميل');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.code || '').toLowerCase().includes(s) ||
           (c.name_en || '').toLowerCase().includes(s) ||
           (c.name_ar || '').toLowerCase().includes(s);
  });

  const seed = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axiosInstance.post('/expense-categories/seed');
      toast.success(`تَم تَركيب ${data.installed} تَصنيف افتِراضي (${data.skipped} مَوجود سَلَفاً)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل التَّركيب');
    }
  };

  const toggleStatus = async (cat) => {
    if (!isAdmin) return;
    const next = cat.status === 'active' ? 'inactive' : 'active';
    try {
      await axiosInstance.put(`/expense-categories/${cat.id}`, { status: next });
      toast.success(next === 'active' ? 'تَم تَفعيل التَّصنيف' : 'تَم تَعطيل التَّصنيف');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل');
    }
  };

  const remove = async (cat) => {
    if (!isAdmin) return;
    if (!window.confirm(`حَذف "${cat.name_ar || cat.name_en}" نِهائياً؟`)) return;
    try {
      await axiosInstance.delete(`/expense-categories/${cat.id}`);
      toast.success('تَم الحَذف');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فَشِل الحَذف');
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="expense-categories-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Tag className="w-7 h-7 text-indigo-600" />
            تَصنيفات المَصاريف (Smart Mapping)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            رَبط كُل تَصنيف بِالحِساب المُحاسَبي ونِسبَة الضَّريبة افتِراضياً — لِتَسريع الإدخال ومَنع الأَخطاء.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={seed}
              data-testid="ec-seed-btn"
              className="px-3 py-1.5 rounded-lg text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 inline-flex items-center gap-1"
            >
              <Sparkles className="w-3.5 h-3.5" /> تَركيب الباقَة الافتِراضية (KSA)
            </button>
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="ec-new-btn"
              className="px-3 py-1.5 rounded-lg text-xs bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> تَصنيف جَديد
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-200 flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="ec-search"
            placeholder="بَحث بِالكود أَو الاسم..."
            className="bg-transparent border-0 outline-none text-sm flex-1"
          />
        </div>
        <button
          onClick={load}
          data-testid="ec-refresh"
          className="px-3 py-1.5 rounded-lg text-xs bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> تَحديث
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ec-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="p-2 text-start">الكود</th>
                <th className="p-2 text-start">الاسم</th>
                <th className="p-2 text-start">النَّوع</th>
                <th className="p-2 text-start">الحِساب المُحاسَبي</th>
                <th className="p-2 text-end">VAT %</th>
                <th className="p-2 text-center">الحالة</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline me-2" />جارٍ التَّحميل...
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">
                  لا تُوجَد تَصنيفات بَعد. اضغَط <strong>تَركيب الباقَة الافتِراضية</strong>.
                </td></tr>
              ) : filtered.map((c) => (
                <tr key={c.id} data-testid={`ec-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-2 font-mono text-xs text-slate-700 flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${TONE_DOT[c.color] || TONE_DOT.slate}`} />
                    {c.code}
                    {c.is_system && <Lock className="w-3 h-3 text-slate-400" title="نِظامي — لا يُحذَف" />}
                  </td>
                  <td className="p-2 text-xs text-slate-900">{c.name_ar || c.name_en}</td>
                  <td className="p-2 text-xs text-slate-500">{KIND_LABEL[c.default_bill_kind] || c.default_bill_kind}</td>
                  <td className="p-2 text-xs text-slate-500">
                    {c.default_account_code ? `${c.default_account_code} · ${c.default_account_name || ''}` : '—'}
                  </td>
                  <td className="p-2 text-end text-xs font-mono">{c.default_vat_rate}%</td>
                  <td className="p-2 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      c.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'
                    }`}>{c.status === 'active' ? 'فَعّال' : 'مُعَطَّل'}</span>
                  </td>
                  <td className="p-2 text-center">
                    {isAdmin && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => { setEditing(c); setShowForm(true); }}
                          data-testid={`ec-edit-${c.id}`}
                          className="p-1 rounded hover:bg-indigo-100 text-indigo-700"
                        ><Edit2 className="w-3.5 h-3.5" /></button>
                        <button
                          onClick={() => toggleStatus(c)}
                          data-testid={`ec-toggle-${c.id}`}
                          className="p-1 rounded hover:bg-amber-100 text-amber-700"
                          title={c.status === 'active' ? 'تَعطيل' : 'تَفعيل'}
                        ><Power className="w-3.5 h-3.5" /></button>
                        {!c.is_system && (
                          <button
                            onClick={() => remove(c)}
                            data-testid={`ec-delete-${c.id}`}
                            className="p-1 rounded hover:bg-red-100 text-red-700"
                          ><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <ExpenseCategoryForm
          editing={editing}
          accounts={accounts}
          costCenters={costCenters}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


// ─── modal form ────────────────────────────────────────────────────


const ExpenseCategoryForm = ({ editing, accounts, costCenters, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    code:                  editing?.code || '',
    name_en:               editing?.name_en || '',
    name_ar:               editing?.name_ar || '',
    default_bill_kind:     editing?.default_bill_kind || 'supplier',
    default_account_id:    editing?.default_account_id || '',
    default_vat_rate:      editing?.default_vat_rate ?? 15,
    default_cost_center_id:editing?.default_cost_center_id || '',
    default_payment_method:editing?.default_payment_method || '',
    color:                 editing?.color || 'slate',
    sort_order:            editing?.sort_order ?? 100,
  }));
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name_en) {
      toast.error('الكود والاسم الإنجليزي مَطلوبان');
      return;
    }
    try {
      setSubmitting(true);
      // Find the selected account to denormalise code/name onto the row.
      const acc = accounts.find((a) => a.id === form.default_account_id);
      const payload = {
        ...form,
        default_vat_rate: Number(form.default_vat_rate) || 0,
        sort_order:       Number(form.sort_order) || 100,
        default_account_code: acc?.code || null,
      };
      if (editing) {
        await axiosInstance.put(`/expense-categories/${editing.id}`, payload);
        toast.success('تَم التَّحديث');
      } else {
        await axiosInstance.post('/expense-categories', payload);
        toast.success('تَم الإنشاء');
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فَشِل الحِفظ');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ec-form-modal" role="dialog" aria-modal="true">
      <form
        onSubmit={save}
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
      >
        <div className="p-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <h3 className="font-bold text-lg">{editing ? 'تَعديل التَّصنيف' : 'تَصنيف مَصاريف جَديد'}</h3>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">الكود *</span>
              <input
                type="text" value={form.code} onChange={onChange('code')}
                disabled={editing?.is_system}
                data-testid="ec-form-code"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">الاسم (EN) *</span>
              <input
                type="text" value={form.name_en} onChange={onChange('name_en')}
                data-testid="ec-form-name-en"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">الاسم (AR)</span>
            <input
              type="text" value={form.name_ar} onChange={onChange('name_ar')}
              data-testid="ec-form-name-ar"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">النَّوع</span>
              <select value={form.default_bill_kind} onChange={onChange('default_bill_kind')}
                data-testid="ec-form-kind"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                {Object.entries(KIND_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">VAT %</span>
              <input type="number" step="0.01" value={form.default_vat_rate}
                onChange={onChange('default_vat_rate')}
                data-testid="ec-form-vat"
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">الحِساب المُحاسَبي الافتِراضي</span>
            <select value={form.default_account_id} onChange={onChange('default_account_id')}
              data-testid="ec-form-account"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">— لا شَيء —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name_arabic || a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-700">مَركَز التَّكلفة الافتِراضي</span>
            <select value={form.default_cost_center_id} onChange={onChange('default_cost_center_id')}
              data-testid="ec-form-cc"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">— لا شَيء —</option>
              {costCenters.map((c) => (
                <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="p-4 bg-slate-50 border-t flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            data-testid="ec-form-cancel"
            className="px-4 py-2 rounded-lg text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"
          >إِلغاء</button>
          <button type="submit" disabled={submitting}
            data-testid="ec-form-submit"
            className="px-4 py-2 rounded-lg text-sm bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold disabled:opacity-50"
          >{submitting ? '...جارٍ الحِفظ' : 'حِفظ'}</button>
        </div>
      </form>
    </div>
  );
};
